/**
 * SiteMarkers — lettered-circle markers for the multi-site MapLibre
 * surfaces (EvaluationMap, ComparisonMap). Thin wrapper around the
 * existing `LocationMarker` primitive: maps each site to a marker with
 * a sequential letter label (A, B, …, Z, AA, AB, …) and the matching
 * indexed colour from `siteColorForIndex`.
 *
 * `LocationMarker` already supports a `label` prop and applies
 * matching styling — this component just owns the indexing rule and
 * the site → location-shape mapping.
 *
 * Linear: MYD-96 (I2). F6 (ComparisonMap) currently maps sites to
 * `LocationMarker` inline; a closing-sweep PR can swap F6 to use this
 * wrapper.
 */

import type maplibregl from 'maplibre-gl'
import { LocationMarker } from './LocationMarker'
import { siteColorForIndex } from '../config/mapConfig'

export interface SiteForMarker {
	_id?: string
	id?: string
	name?: string
	assetName?: string
	address?: string
	city?: string
	state?: string
	stateProvince?: string
	latitude: number
	longitude: number
	imageUrls?: string[]
	image?: string
	/**
	 * Derived server-side from the site's most recent uploaded SiteImage.
	 * Preferred over imageUrls, which only holds the URL typed into the site
	 * form and is replaced wholesale whenever that form is saved.
	 */
	coverImage?: string
	/**
	 * The coordinates are an area centre (city / postal code) rather than the
	 * site's own position, because its address could not be geocoded. Drawn
	 * dashed and faded, and called out in the popup.
	 */
	isApproximate?: boolean
	/** The area the pin is the centre of, e.g. "78701, Austin, Texas". */
	approxLabel?: string | null
}

export interface SiteMarkersProps {
	map: maplibregl.Map | null
	sites: SiteForMarker[]
	onMarkerClick?: (siteId: string) => void
	/** Optional second popup action, routing to the site's own screen. */
	onGotoLocation?: (siteId: string) => void
}

/**
 * Map a 0-based site index to a marker label. A–Z then AA, AB, …
 * defensive past 26 sites; the practical cap on the comparison surface
 * is much lower.
 */
export function letterForIndex(index: number): string {
	if (index < 26) return String.fromCharCode(65 + index)
	const first = Math.floor(index / 26) - 1
	const second = index % 26
	return `${String.fromCharCode(65 + first)}${String.fromCharCode(65 + second)}`
}

export function SiteMarkers({ map, sites, onMarkerClick, onGotoLocation }: SiteMarkersProps) {
	return (
		<>
			{sites.map((site, idx) => {
				const id = site._id ?? site.id
				if (!id) return null
				if (
					typeof site.latitude !== 'number' ||
					typeof site.longitude !== 'number' ||
					!Number.isFinite(site.latitude) ||
					!Number.isFinite(site.longitude)
				) {
					return null
				}
				return (
					<LocationMarker
						key={id}
						map={map}
						location={{
							id,
							assetName: site.name ?? site.assetName ?? '',
							address: site.address,
							city: site.city,
							stateProvince: site.state ?? site.stateProvince,
							latitude: site.latitude,
							longitude: site.longitude,
							image: site.coverImage ?? site.imageUrls?.[0] ?? site.image,
						}}
						color={siteColorForIndex(idx)}
						label={letterForIndex(idx)}
						isApproximate={site.isApproximate}
						approximateLabel={site.approxLabel ?? undefined}
						onViewDetails={onMarkerClick}
						onGotoLocation={onGotoLocation}
					/>
				)
			})}
		</>
	)
}

export default SiteMarkers
